import {
  CurrencyFormatted,
  DrawerDescription,
  DateFormatted,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerClose,
  DrawerTitle,
  Typography,
  Separator,
  Button,
  Drawer,
  Anchor,
} from '@ui/components';
import { DashboardAccountResponse, DashboardTopUpResponse } from '@interfaces/models';
import { CheckImpersonation, HasPermission } from '@shared/components';
import { Cancel, Wallet } from '@ui/components/icons';
import { useCurrentUser } from '@shared/hooks';
import { Link } from 'react-router-dom';
import { Routes } from '@shared/enums';
import { t } from 'i18next';
import React from 'react';

import { maskIban } from './utils';

interface DashboardWalletDrawerProps {
  accounts: DashboardAccountResponse[];
  topUps: DashboardTopUpResponse[];
  isOpen: boolean;
  onCtaClick: () => void;
  onClose: () => void;
}

export const DashboardWalletDrawer = (props: DashboardWalletDrawerProps) => {
  const { topUps, accounts, isOpen, onClose, onCtaClick } = props;
  const { companyId } = useCurrentUser();

  return (
    <Drawer onOpenChange={onClose} open={isOpen} handleOnly>
      <DrawerContent className="flex flex-col gap-0 overflow-hidden p-0" draggable="false">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
          <DrawerTitle>{t('DASHBOARD.WALLET.DRAWER.ACCOUNT_DETAILS')}</DrawerTitle>
          <DrawerDescription />
          <DrawerClose asChild>
            <Button variant="ghost" size="small" icon>
              <Cancel />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="p-4 overflow-auto">
          <div className="grid grid-cols-6 gap-x-4 gap-y-6">
            {accounts.length > 0 ? (
              accounts.map((account, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <Typography className="text-weak col-span-2" variant="labelSmallBook">
                      {t('DASHBOARD.WALLET.DRAWER.ACCOUNT_NUMBER', { number: idx + 1 })}
                    </Typography>
                    <Typography variant="labelMediumBook" className="col-span-4">
                      {account.iban}
                    </Typography>
                  </React.Fragment>
                );
              })
            ) : (
              <Typography variant="labelMediumBook" className="col-span-6">
                {t('DASHBOARD.WALLET.DRAWER.NO_ACCOUNT')}
              </Typography>
            )}
          </div>

          <HasPermission permissionFeature="DASHBOARD_ACCOUNT_TOP_UP" permissionType="READ">
            <Separator className="my-6" />
            <div className="flex items-center gap-2 mb-6">
              <Wallet />
              <Typography variant="h200">{t('DASHBOARD.WALLET.DRAWER.TOP_UP')}</Typography>
            </div>

            {topUps.length > 0 ? (
              topUps.map((topUp, idx) => {
                return (
                  <div
                    className="grid grid-cols-6 gap-4 py-4 border-b border-border-default"
                    key={idx}
                  >
                    <Typography className="text-weak col-span-2" variant="labelMediumBook">
                      <DateFormatted className="col-span-2" date={topUp.date} />
                    </Typography>
                    <Typography variant="labelMediumBook" className="col-span-2">
                      {maskIban(topUp.iban)}
                    </Typography>
                    <Typography
                      className="col-span-2 text-right text-success"
                      variant="labelMediumBook"
                    >
                      +<CurrencyFormatted value={topUp.amount} />
                    </Typography>
                  </div>
                );
              })
            ) : (
              <Typography variant="labelMediumBook" className="col-span-6">
                {t('DASHBOARD.WALLET.DRAWER.NO_TOP_UP')}
              </Typography>
            )}
            <Anchor asChild>
              <Link
                to={`/${companyId}/${Routes.FILES_REPORTS}/${Routes.REPORTS}`}
                className="block mt-2"
              >
                <Button variant="link" wide>
                  {t('DASHBOARD.WALLET.DRAWER.REPORT_BUTTON')}
                </Button>
              </Link>
            </Anchor>
          </HasPermission>
        </div>

        <DrawerFooter>
          <CheckImpersonation>
            <HasPermission permissionFeature="DASHBOARD_ACCOUNT_TOP_UP" permissionType="WRITE">
              <Button onClick={onCtaClick} size="large" wide>
                {t('DASHBOARD.WALLET.DRAWER.TOP_UP_BUTTON')}
              </Button>
            </HasPermission>
          </CheckImpersonation>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
